import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { useSelectedOrganization } from "@/hooks/useCurrentUser";
import type { Brand } from "@/hooks/useGetBrands";

/** SVG is allowed because logos are usually vector, and every surface draws the
 *  mark through <img>, where an SVG's scripts never run. */
const EXTENSION_BY_MIME: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/svg+xml": "svg",
};

const MIME_BY_EXTENSION: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  svg: "image/svg+xml",
};

export const BRAND_LOGO_ACCEPT = Object.keys(EXTENSION_BY_MIME).join(",");

export const MAX_BRAND_LOGO_BYTES = 5 * 1024 * 1024;

type LogoUploadUrlResponse = { uploadUrl: string; uploadToken: string };

function extensionOf(fileName: string): string {
  const idx = fileName.lastIndexOf(".");
  return idx >= 0 ? fileName.slice(idx + 1).toLowerCase() : "";
}

/**
 * Uploads a brand's logo through the backend's presigned flow: mint an upload
 * URL, PUT the file straight to S3, then confirm so the brand row records the key.
 *
 * The confirm answers with the brand and a fresh presigned `logoUrl`, which is
 * written into the cached brand list — the one read the brand picker, the
 * universe core and Home all draw from — so the mark changes everywhere at once.
 * No invalidation after it: a refetch would presign the same key again and swap
 * every <img> to a new URL for an identical file.
 */
export function useUploadBrandLogo(brandId: string | null) {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useSelectedOrganization();
  const orgId = selectedOrganization?.organization.id ?? null;

  return useMutation({
    mutationFn: async (file: File): Promise<Brand> => {
      if (!brandId) throw new Error("No brand selected");

      const mimetype = file.type || MIME_BY_EXTENSION[extensionOf(file.name)] || "";
      const extension = EXTENSION_BY_MIME[mimetype];
      if (!extension) throw new Error("Use a PNG, JPG, WebP or SVG file.");
      if (file.size > MAX_BRAND_LOGO_BYTES) {
        throw new Error(`The logo is too large (max ${MAX_BRAND_LOGO_BYTES / (1024 * 1024)} MB).`);
      }

      const { data: ticket } = await axiosInstance.post<LogoUploadUrlResponse>(
        `/brand/${brandId}/logo/upload-url`,
        { filename: file.name, mimetype, extension },
      );

      // The PUT must carry exactly the Content-Type the presign was minted with.
      const put = await fetch(ticket.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": mimetype },
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);

      const { data } = await axiosInstance.post<Brand>(`/brand/${brandId}/logo/confirm`, {
        uploadToken: ticket.uploadToken,
      });
      return data;
    },
    onSuccess: (brand) => {
      queryClient.setQueryData<Brand[]>(["brands", orgId], (old) =>
        old?.map((b) => (b.id === brand.id ? { ...b, logoUrl: brand.logoUrl } : b)),
      );
    },
  });
}
